import { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { auth, googleProvider } from '../firebaseConfig';
import {
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signOut,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail
} from 'firebase/auth';
import './Login.css';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import GoogleIcon from './icons/GoogleIcon';
import TelegramLoginButton from './TelegramLoginButton';
import { GAUtils } from '../model/GAUtils';
// import AppleIcon from './icons/AppleIcon';

export const Login = () => {
    const navigate = useNavigate();
    const [user, loading] = useAuthState(auth);
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSignUp, setIsSignUp] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showAltLoginHint, setShowAltLoginHint] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const loginMethodRef = useRef<'google' | 'email' | 'telegram'>('google');

    // Track page view on mount
    useEffect(() => {
        GAUtils.trackPageView('login', 'Login - XTEA');
    }, []);

    // Handle redirect result (for when signInWithPopup falls back to signInWithRedirect)
    useEffect(() => {
        getRedirectResult(auth)
            .then((result) => {
                if (result) {
                    loginMethodRef.current = 'google';
                    navigate('/');
                }
            })
            .catch((error: any) => {
                console.error('Error handling redirect result:', error);
                GAUtils.trackLoginError('google', error.message || 'Redirect error', error.code);
                setError(getErrorMessage(error.code));
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (user) {
            // Track successful login
            GAUtils.trackLoginSuccess(loginMethodRef.current, user.uid);
            navigate('/');
        }
    }, [user, navigate]);

    const handleGoogleSignIn = async () => {
        try {
            setIsSigningIn(true);
            setError(null);
            loginMethodRef.current = 'google';
            // Track login attempt
            GAUtils.trackLoginAttempt('google');
            await signInWithPopup(auth, googleProvider);
        } catch (error: any) {
            const popupErrors = ['auth/popup-closed-by-user', 'auth/popup-blocked', 'auth/cancelled-popup-request'];
            if (popupErrors.includes(error.code)) {
                console.log('Popup blocked/closed, falling back to redirect...');
                try {
                    await signInWithRedirect(auth, googleProvider);
                    return; // redirect will navigate away
                } catch (redirectError: any) {
                    console.error("Error with redirect sign-in:", redirectError);
                    GAUtils.trackLoginError('google', redirectError.message || 'Redirect error', redirectError.code);
                    setError(getErrorMessage(redirectError.code));
                    return;
                }
            }
            console.error("Error signing in with Google:", error);
            GAUtils.trackLoginError('google', error.message || 'Unknown error', error.code);
            setError(getErrorMessage(error.code));
        } finally {
            setIsSigningIn(false);
        }
    };

    const handleEmailAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !password) {
            setError('Please enter both email and password');
            return;
        }

        try {
            setIsSigningIn(true);
            setError(null);
            loginMethodRef.current = 'email';
            GAUtils.trackLoginAttempt('email');

            if (isSignUp) {
                await createUserWithEmailAndPassword(auth, email, password);
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
        } catch (error: any) {
            console.error("Error with email auth:", error);
            GAUtils.trackLoginError('email', error.message || 'Unknown error', error.code);
            setError(getErrorMessage(error.code));
            if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found') {
                setShowAltLoginHint(true);
            }
        } finally {
            setIsSigningIn(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!email) {
            setError('Please enter your email address first');
            return;
        }

        try {
            setIsSigningIn(true);
            setError(null);
            await sendPasswordResetEmail(auth, email);
            setSuccessMessage('Password reset email sent! Check your inbox.');
            GAUtils.trackButtonClick('forgot_password', 'login');
        } catch (error: any) {
            console.error("Error sending reset email:", error);
            setError(getErrorMessage(error.code));
        } finally {
            setIsSigningIn(false);
        }
    };

    const getErrorMessage = (errorCode: string): string => {
        switch (errorCode) {
            case 'auth/invalid-email':
                return 'Invalid email address';
            case 'auth/user-disabled':
                return 'This account has been disabled';
            case 'auth/user-not-found':
                return 'No account found with this email';
            case 'auth/wrong-password':
                return 'Incorrect password';
            case 'auth/invalid-credential':
                return 'Invalid email or password';
            case 'auth/email-already-in-use':
                return 'An account with this email already exists';
            case 'auth/weak-password':
                return 'Password should be at least 6 characters';
            case 'auth/too-many-requests':
                return 'Too many attempts. Please try again later';
            case 'auth/popup-closed-by-user':
                return 'Sign-in was cancelled';
            case 'auth/network-request-failed':
                return 'Network error. Please check your connection and try again';
            case 'auth/operation-not-allowed':
                return 'This sign-in method is not enabled. Please try another option';
            default:
                return 'An error occurred. Please try again';
        }
    };

    // const handleAppleSignIn = async () => {
    //     try {
    //         setIsSigningIn(true);
    //         GAUtils.trackLoginAttempt('apple');
    //         await signInWithPopup(auth, appleProvider);
    //     } catch (error: any) {
    //         console.error("Error signing in with Apple:", error);
    //         GAUtils.trackLoginError('apple', error.message || 'Unknown error');
    //     } finally {
    //         setIsSigningIn(false);
    //     }
    // };

    const handleSignOut = async () => {
        try {
            // Track logout
            GAUtils.trackLogout(user?.uid);
            await signOut(auth);
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    if (loading) {
        return (
            <div className="login-container">
                <div className="login-loading">
                    <div className="loading-spinner"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-container">
            <Helmet>
                <title>XTEA Pro Sign In - Telegram Search Engine for Channels, Groups & Bots</title>
                <meta name="description" content="Sign in to XTEA Pro to unlock advanced Telegram search: channels, groups, and bots. Fast results, better filtering, and credit-based Pro access." />
                <meta name="keywords" content="XTEA Pro, Telegram search engine, Telegram channels, Telegram groups, Telegram bots, Telegram Pro search" />
                <link rel="canonical" href="https://xtea.app/login" />
                <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
                <meta property="og:title" content="XTEA Pro Sign In - Telegram Search Engine" />
                <meta property="og:description" content="Sign in to XTEA Pro for advanced Telegram search across channels, groups, and bots." />
                <meta property="og:url" content="https://xtea.app/login" />
                <meta property="og:site_name" content="XTEA Pro" />
                <meta name="twitter:title" content="XTEA Pro Sign In - Telegram Search Engine" />
                <meta name="twitter:description" content="Sign in to XTEA Pro for advanced Telegram search across channels, groups, and bots." />
                <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'SoftwareApplication',
                        name: 'XTEA Pro',
                        applicationCategory: 'SearchApplication',
                        operatingSystem: 'Web',
                        url: 'https://xtea.app/login',
                        description: 'XTEA Pro is a Telegram search engine for discovering channels, groups, and bots quickly with advanced filtering.',
                        offers: {
                            '@type': 'Offer',
                            price: '0',
                            priceCurrency: 'USD',
                        },
                        publisher: {
                            '@type': 'Organization',
                            name: 'XTEA',
                            url: 'https://xtea.app',
                        },
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: [
                            {
                                '@type': 'Question',
                                name: 'What is XTEA Pro?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'XTEA Pro is a Telegram search engine that helps you discover channels, groups, and bots with better relevance and filtering.',
                                },
                            },
                            {
                                '@type': 'Question',
                                name: 'Do I need an account to use XTEA Pro?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes. XTEA Pro requires sign in so we can provide personalized results, saved preferences, and account-based credits.',
                                },
                            },
                        ],
                    })}
                </script>
            </Helmet>
            {/* Left Side - Product Introduction */}
            <div className="login-hero">
                <div className="hero-content">
                    <div className="hero-logo">
                        <h1>XTEA</h1>
                        <span className="hero-tagline">Telegram Search Engine</span>
                    </div>
                    
                    <div className="hero-title">
                        <h2>The #1 Telegram Search Engine</h2>
                        <p className="hero-description">
                            Discover relevant Telegram content instantly with accurate results.
                        </p>
                        <p className="hero-description" style={{ marginTop: '12px', opacity: 0.9 }}>
                            XTEA Pro helps you find Telegram channels, groups, and bots faster with advanced search quality and Pro credits.
                        </p>
                        <div style={{ marginTop: '12px', fontSize: '14px', opacity: 0.9 }}>
                            Learn more: <a href="/about">About</a> · <a href="/terms">Terms</a> · <a href="/privacy">Privacy</a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side - Login Form */}
            <div className="login-form-container">
                <div className="login-card">
                    {!user ? (
                        <>
                            <div className="login-header">
                                <h1>Welcome back</h1>
                                <p className="login-subtitle">
                                    Sign in to continue
                                </p>
                            </div>

                            <div className="login-content">
                                {/* Error/Success Messages */}
                                {error && (
                                    <div className="auth-message error-message">
                                        {error}
                                    </div>
                                )}
                                {showAltLoginHint && (
                                    <div className="auth-message hint-message">
                                        Did you sign up with Google or Telegram? Try those options below.
                                    </div>
                                )}
                                {successMessage && (
                                    <div className="auth-message success-message">
                                        {successMessage}
                                    </div>
                                )}

                                {/* Email Form */}
                                <form onSubmit={handleEmailAuth} className="email-form">
                                    <div className="form-group">
                                        <input
                                            type="email"
                                            placeholder="Email address"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="form-input"
                                            disabled={isSigningIn}
                                        />
                                    </div>
                                    <div className="form-group password-group">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="form-input"
                                            disabled={isSigningIn}
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPassword(v => !v)}
                                            tabIndex={-1}
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        >
                                            {showPassword ? '🙈' : '👁️'}
                                        </button>
                                    </div>
                                    {!isSignUp && (
                                        <button
                                            type="button"
                                            className="forgot-password-link"
                                            onClick={handleForgotPassword}
                                            disabled={isSigningIn}
                                        >
                                            Forgot password?
                                        </button>
                                    )}
                                    <button 
                                        type="submit"
                                        className="auth-button email-button"
                                        disabled={isSigningIn}
                                    >
                                        {isSigningIn ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign In')}
                                    </button>
                                </form>

                                <div className="auth-toggle">
                                    <span>{isSignUp ? 'Already have an account?' : "Don't have an account?"}</span>
                                    <button
                                        type="button"
                                        className="toggle-button"
                                        onClick={() => {
                                            setIsSignUp(!isSignUp);
                                            setError(null);
                                            setSuccessMessage(null);
                                            setShowAltLoginHint(false);
                                        }}
                                    >
                                        {isSignUp ? 'Sign In' : 'Sign Up'}
                                    </button>
                                </div>

                                <div className="divider">
                                    <span>or continue with</span>
                                </div>

                                <div className="auth-buttons">
                                    <button 
                                        className="auth-button google-button"
                                        onClick={handleGoogleSignIn}
                                        disabled={isSigningIn}
                                    >
                                        <GoogleIcon />
                                        <span>Google</span>
                                    </button>
                                    
                                    {/* <button 
                                        className="auth-button apple-button"
                                        onClick={handleAppleSignIn}
                                        disabled={isSigningIn}
                                    >
                                        <AppleIcon />
                                        <span>Apple</span>
                                    </button> */}
                                </div>

                                <TelegramLoginButton
                                    onError={(msg) => setError(msg || null)}
                                    onSigningIn={(signing) => {
                                        setIsSigningIn(signing);
                                        if (signing) loginMethodRef.current = 'telegram';
                                    }}
                                    disabled={isSigningIn}
                                />

                                {isSigningIn && (
                                    <div className="signing-in-indicator">
                                        <div className="loading-spinner small"></div>
                                        <span>Signing in...</span>
                                    </div>
                                )}

                                <div className="login-footer">
                                    <p>By continuing, you agree to our <a href="/terms">Terms</a> and <a href="/privacy">Privacy Policy</a></p>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="profile-section">
                            <div className="profile-info">
                                {user.photoURL && (
                                    <img 
                                        src={user.photoURL} 
                                        alt="Profile" 
                                        className="profile-image"
                                    />
                                )}
                                <h2>Welcome back!</h2>
                                <p className="profile-name">{user.displayName || 'User'}</p>
                                <p className="profile-email">{user.email}</p>
                            </div>
                            <button 
                                className="auth-button signout-button"
                                onClick={handleSignOut}
                            >
                                Sign Out
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Login;